<template>
	<div id="top">
		<nav class="main-nav fix-on-scroll" :class="{ 'show-mobile-nav': showMobileNav }">
			<div class="row">
				<div class="columns column12">
					<div class="nav-bar">
						<nuxt-link class="logo" :to="{ path: localePath('/', locale), hash: '#top' }">
							<img src="~/assets/images/logo-brasserie-6.svg" alt="Brasserie Logo" />
						</nuxt-link>

						<div class="nav-content">
							<ul>
								<li v-for="item in topmenu[locale]" :key="item.filename" @click="hideMobileNav">
									<nuxt-link :to="item.filename">
										{{ item.header }}
									</nuxt-link>
								</li>
							</ul>
						</div>
						<div class="buttons">
							<div class="menu-button" @click="toggleMobileNav">
								<font-awesome-icon
									class="icon"
									:class="{ hidden: showMobileNav }"
									icon="fal fa-bars"
									size="1x"
								/>
								<font-awesome-icon
									class="icon"
									:class="{ hidden: !showMobileNav }"
									icon="fal fa-xmark"
									size="1x"
								/>
							</div>

							<book-button class="button book-now cta reserve-now">
								<font-awesome-icon class="icon" icon="fal fa-calendar-alt" size="1x" />
								<span>{{ $t('bookNow') }}</span>
							</book-button>
						</div>
					</div>
				</div>
			</div>
		</nav>
	</div>
</template>

<script setup>
const { fetchPage } = usePage();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();

const showMobileNav = ref(false);

const { data: topmenu } = await useWebsiteFetch('topmenu');

const toggleMobileNav = () => {
	showMobileNav.value = !showMobileNav.value;
};

const hideMobileNav = () => {
	showMobileNav.value = false;
};
</script>

<style lang="scss" scoped>
.hidden {
	display: none !important;
}

.main-nav {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	background: #fff;
	box-shadow: 0 0 20px rgb(0 0 0 / 10%);

	ul li {
		display: inline-block;
	}
}

.nav-bar {
	display: flex;
	height: 90px;
	align-items: center;
	padding: 8px 0;
	z-index: 9;
	position: relative;

	.buttons {
		display: flex;
		align-items: center;
		gap: 10px;
		z-index: 1;

		.button {
			padding: 15px 25px;
			margin: 0;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.icon {
			margin-right: 12px;
		}
	}
}

.logo {
	position: absolute;
	top: 0;
	padding: 0;
	text-decoration: none;
	margin: 18px 0;
	width: 185px;
	height: 144px;
	flex-shrink: 0;
	background: #fff;
	box-shadow: 0 15px 15px 0 rgb(0 0 0 / 15%);
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease-in-out;
}

.logo img {
	max-width: 150px;
	height: auto;
	transition: max-width 0.3s ease-in-out;
}

.fix-on-scroll.fixed {
	.logo {
		height: 70px;
		margin: 12px 0;
		box-shadow: 0 0 0 0 rgb(0 0 0 / 0%);
		transform: translateX(-65px);
	}

	.logo img {
		max-width: 55px;
	}
}

.nav-content {
	flex-grow: 1;
	text-align: right;
	margin-right: 20px;

	a {
		text-decoration: none;
		margin: 0 15px;
		color: #111;
		letter-spacing: 1px;
		font-family: var(--secondary-font-family);
		font-size: 15px;
		font-weight: 700;
		border-bottom: 2px solid #fff;

		&.router-link-active,
		&:hover {
			color: var(--cta-color) !important;
		}
	}

	.menu-close {
		color: #000;
		display: none;
		font-size: 30px;
		cursor: pointer;
		position: absolute;
		top: 15px;
		width: auto;
		right: 20px;
		padding: 0;
		margin: 0;
	}
}

.menu-button,
.menu-close {
	display: none;
	transition: color 0.3s ease-in-out;
}

.menu-button:hover,
.menu-close:hover {
	color: var(--header-color);
}

@media (max-width: 1280px) {
	.nav-bar a {
		font-size: 14px;
	}
}

@media (max-width: 1200px) {
	.menu-button {
		text-transform: uppercase;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: space-between;
		margin: 0 10px 0 0;
		cursor: pointer;

		svg {
			font-size: 22px;
		}

		span {
			margin: 0 0 0 5px;
		}
	}

	.nav-bar .buttons .icon {
		display: unset;
	}

	.main-nav {
		position: fixed !important;
		top: 0;
		box-shadow: 0 0 10px rgb(0 0 0 / 10%);

		&.show-mobile-nav {
			.nav-content ul {
				display: flex;
				flex-direction: column;
				justify-content: center;
				opacity: 1;
				visibility: visible;
				transition: opacity 0.3s ease-in-out;

				li:not(.menu-close) {
					width: 100%;
				}

				li:not(.lang-nav, .menu-close) {
					margin: 7px 0;

					a {
						font-size: 24px;
						color: var(--header-color);
					}

					a:hover {
						color: var(--footer-hover-color);
					}
				}
			}
		}

		ul {
			position: fixed;
			inset: 0;
			color: var(--header-color);
			background: var(--primary-background-color);
			text-align: center;
			padding: 20px;
			opacity: 0;
			visibility: hidden;
			overflow-y: scroll;
		}
	}

	.nav-content .menu-close {
		display: block;
	}

	.logo {
		h3 {
			font-size: 22px;
		}
	}
}

@media (max-width: 880px) {
	.nav-bar .buttons {
		.button {
			padding: 16px;
		}

		.menu-button {
			margin-right: 5px;

			.icon {
				font-size: 22px;
			}
		}

		.icon {
			margin-right: 0;
			font-size: 16px;
		}

		span {
			display: none;
		}
	}
}

@media (max-width: 580px) {
	.nav-bar .buttons span {
		display: none;
	}

	.nav-bar .logo {
		font-size: 22px;
	}
}
</style>
